import { useClient } from '@sunucupark/client-react';
import { Dialog, Kbd } from '@sunucupark/ui';
import { useTranslation } from 'react-i18next';

export interface ShortcutHelpProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Spells a binding the way the reader will press it: `g i`, `mod+k` → ⌘K or Ctrl+K. */
function spell(binding: string, isMac: boolean): string[] {
  return binding.split(' ').map((chord) =>
    chord
      .split('+')
      .map((part) => (part === 'mod' ? (isMac ? '⌘' : 'Ctrl') : part === 'shift' ? '⇧' : part))
      .join(isMac ? '' : '+'),
  );
}

/**
 * Everything the keyboard can do, straight from the registry — so a shortcut
 * the user rebound, or one a feature added, is listed without anyone updating
 * this dialog (ADR 0006).
 */
export function ShortcutHelp({ open, onOpenChange }: ShortcutHelpProps) {
  const { t } = useTranslation();
  const { shortcuts } = useClient();
  const isMac = /mac/i.test(navigator.userAgent);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('shortcuts.title')}
      description={t('shortcuts.description')}
      closeLabel={t('app.close')}
      size="lg"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        {shortcuts.help().map((group) => (
          <section key={group.groupKey} aria-labelledby={`group-${group.groupKey}`}>
            <h3 id={`group-${group.groupKey}`} className="mb-2 text-caption-strong text-text-secondary">
              {t(group.groupKey)}
            </h3>
            <ul className="space-y-1">
              {group.entries.map((entry) => (
                <li key={entry.id} className="flex items-center justify-between gap-3 text-subhead">
                  <span className="text-text">{t(entry.labelKey)}</span>
                  <span className="flex shrink-0 gap-1">
                    {spell(entry.keys[0] ?? '', isMac).map((chord, index) => (
                      <Kbd key={`${entry.id}-${index}`}>{chord}</Kbd>
                    ))}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </Dialog>
  );
}
